import { Vec3 } from './vec3';

export class AABB {
  constructor(public min = new Vec3(Infinity, Infinity, Infinity), public max = new Vec3(-Infinity, -Infinity, -Infinity)) {}
  static fromCenterSize(c: Vec3, s: Vec3): AABB {
    return new AABB(new Vec3(c.x - s.x / 2, c.y - s.y / 2, c.z - s.z / 2), new Vec3(c.x + s.x / 2, c.y + s.y / 2, c.z + s.z / 2));
  }
  static fromMinMax(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number): AABB {
    return new AABB(new Vec3(minX, minY, minZ), new Vec3(maxX, maxY, maxZ));
  }
  set(min: Vec3, max: Vec3): this { this.min.copy(min); this.max.copy(max); return this; }
  copy(b: AABB): this { this.min.copy(b.min); this.max.copy(b.max); return this; }
  clone(): AABB { return new AABB(this.min.clone(), this.max.clone()); }
  isEmpty(): boolean { return this.min.x > this.max.x; }
  expandByPoint(p: Vec3): this { this.min.min(p); this.max.max(p); return this; }
  expandByScalar(s: number): this { this.min.x -= s; this.min.y -= s; this.min.z -= s; this.max.x += s; this.max.y += s; this.max.z += s; return this; }
  union(b: AABB): this { this.min.min(b.min); this.max.max(b.max); return this; }
  center(out = new Vec3()): Vec3 { return out.set((this.min.x + this.max.x) / 2, (this.min.y + this.max.y) / 2, (this.min.z + this.max.z) / 2); }
  size(out = new Vec3()): Vec3 { return out.set(this.max.x - this.min.x, this.max.y - this.min.y, this.max.z - this.min.z); }
  containsPoint(p: Vec3): boolean {
    return p.x >= this.min.x && p.x <= this.max.x && p.y >= this.min.y && p.y <= this.max.y && p.z >= this.min.z && p.z <= this.max.z;
  }
  intersects(b: AABB): boolean {
    return !(b.max.x < this.min.x || b.min.x > this.max.x || b.max.y < this.min.y || b.min.y > this.max.y || b.max.z < this.min.z || b.min.z > this.max.z);
  }
  /** Closest point on/in the box to p. */
  closestPoint(p: Vec3, out = new Vec3()): Vec3 {
    out.x = Math.max(this.min.x, Math.min(this.max.x, p.x));
    out.y = Math.max(this.min.y, Math.min(this.max.y, p.y));
    out.z = Math.max(this.min.z, Math.min(this.max.z, p.z));
    return out;
  }
  intersectsSphere(c: Vec3, r: number): boolean {
    const q = this.closestPoint(c, _tmp);
    return q.distanceToSq(c) <= r * r;
  }
  translate(v: Vec3): this { this.min.add(v); this.max.add(v); return this; }
}
const _tmp = new Vec3();
